
<template>
  <div class="day-actions">
    <div class="day-actions__buttons core-actions core-actions--right">
      <a
        href="#"
        class="btn day-btn day-btn--start"
        :class="{ disabled: startDisabled }"
        @click.prevent="onStart"
      >
        <span class="day-btn__label">
          {{ startLabel }}
        </span>
      </a>

      <a
        href="#"
        class="btn day-btn day-btn--end"
        :class="{ disabled: endDisabled }"
        @click.prevent="onEnd"
      >
        <span class="day-btn__label">
          {{
            checkingClosing
              ? "Checking..."
              : busyEnd
                ? "Processing..."
                : endLabel
          }}
        </span>
      </a>
    </div>

    <span
      v-if="error"
      class="core-helper day-actions__message red-text"
    >
      {{ error }}
    </span>
  </div>

  <StartDayCheckModal
    ref="startDayCheckModalRef"
    :submitting="busyStart"
    @submit="handleStartDayCheckSubmit"
  />

  <EndDayCheckModal
    ref="endDayCheckModalRef"
    :submitting="busyEnd"
    @submit="handleEndDayCheckSubmit"
  />
</template>

<script setup>
import {
  computed,
  ref,
} from "vue";

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import { db, auth } from "@/firebase";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import { useDayLock } from "@/scripts/useScripts/useDayLock";

import StartDayCheckModal from "@/components/dueDil/StartDayCheckModal.vue";
import EndDayCheckModal from "@/components/dueDil/EndDayCheckModal.vue";

const { venueId } = useVenueContext();

const {
  tradingDate,
  isOpen,
  weekStartPending,
  start,
} = useDayLock(venueId);

start();

const error = ref("");
const busyStart = ref(false);
const busyEnd = ref(false);
const checkingClosing = ref(false);

const startDayCheckModalRef = ref(null);
const endDayCheckModalRef = ref(null);

function asStr(value) {
  return String(value ?? "").trim();
}

function parseDowUTC(ymd) {
  if (!ymd) return -1;

  return new Date(`${ymd}T00:00:00Z`).getUTCDay();
}

const cleanVenueId = computed(
  () => asStr(venueId.value)
);

const cleanTradingDate = computed(
  () => asStr(tradingDate.value)
);

const hasBasics = computed(
  () =>
    !!cleanVenueId.value &&
    !!cleanTradingDate.value
);

const isSunday = computed(
  () => parseDowUTC(cleanTradingDate.value) === 0
);

const startLabel = computed(
  () => weekStartPending.value
    ? "Start Week"
    : "Start Day"
);

const endLabel = computed(
  () => isSunday.value
    ? "End Week"
    : "End Day"
);

const startDisabled = computed(
  () =>
    !hasBasics.value ||
    busyStart.value ||
    busyEnd.value ||
    checkingClosing.value ||
    isOpen.value === true
);

const endDisabled = computed(
  () =>
    !hasBasics.value ||
    busyEnd.value ||
    busyStart.value ||
    checkingClosing.value ||
    isOpen.value !== true
);

function fns() {
  return getFunctions(undefined, "us-central1");
}

function openingRecordRef(venue, date) {
  return doc(
    db,
    "venues",
    venue,
    "start_day_checks",
    date
  );
}

// ---------------------------------------------------------
// START DAY / START WEEK
// ---------------------------------------------------------

function onStart() {
  if (startDisabled.value) return;

  error.value = "";

  startDayCheckModalRef.value?.open();
}

// ---------------------------------------------------------
// END DAY / END WEEK
//
// Only look at this venue's opening record for this date.
// There is no second Venue Closed checkbox.
// ---------------------------------------------------------

async function onEnd() {
  if (endDisabled.value) return;

  error.value = "";
  checkingClosing.value = true;

  const venue = cleanVenueId.value;
  const date = cleanTradingDate.value;

  let closed = false;
  let lookupFailed = false;

  try {
    const openingRecord = await getDoc(
      openingRecordRef(venue, date)
    );

    closed =
      openingRecord.exists() &&
      openingRecord.data()?.venue_closed === true;
  } catch (e) {
    console.error(
      "Unable to read venue opening status:",
      e
    );

    error.value =
      "Unable to verify whether the venue was closed. " +
      (e?.message || String(e));

    lookupFailed = true;
  } finally {
    checkingClosing.value = false;
  }

  if (lookupFailed) return;

  // Prevent a stale response affecting a different venue/day.
  if (
    venue !== cleanVenueId.value ||
    date !== cleanTradingDate.value ||
    isOpen.value !== true
  ) {
    return;
  }

  if (closed) {
    // Venue Closed at opening:
    // record the closing exemption and finish normally.
    await handleEndDayCheckSubmit({
      venue_closed: true,
    });

    return;
  }

  // Normal trading day: original closing form.
  endDayCheckModalRef.value?.open();
}

// ---------------------------------------------------------
// SAVE OPENING CHECKS
// ---------------------------------------------------------

async function saveStartDayCheck(payload, venue, date) {
  const recordRef = openingRecordRef(venue, date);

  const previous = await getDoc(recordRef);

  const venueClosed = payload?.venue_closed === true;

  // Preserve previously submitted checklists.
  if (previous.exists()) {
    const previouslyClosed =
      previous.data()?.venue_closed === true;

    if (previouslyClosed !== venueClosed) {
      throw new Error(
        "An opening checklist with a different venue " +
        "status has already been recorded for this date."
      );
    }

    return;
  }

  await setDoc(recordRef, {
    venue_id: venue,
    trading_date: date,

    // NEW: this is the authoritative status for the day.
    venue_closed: venueClosed,

    checklist_status:
      venueClosed
        ? "venue_closed"
        : "completed",

    record_source:
      venueClosed
        ? "venue_closed"
        : "opening_form",

    // Existing fields and structure are preserved.
    fire_exits_clear:
      venueClosed
        ? null
        : !!payload.fire_exits_clear,

    fire_exits_clear_answer:
      venueClosed
        ? ""
        : payload.fire_exits_clear_answer || "",

    fire_exits_clear_comment:
      venueClosed
        ? ""
        : payload.fire_exits_clear_comment || "",

    cctv_working_recording:
      venueClosed
        ? null
        : !!payload.cctv_working_recording,

    cctv_working_recording_answer:
      venueClosed
        ? ""
        : payload.cctv_working_recording_answer || "",

    cctv_working_recording_comment:
      venueClosed
        ? ""
        : payload.cctv_working_recording_comment || "",

    recent_pest_activity:
      venueClosed
        ? null
        : !!payload.recent_pest_activity,

    recent_pest_activity_answer:
      venueClosed
        ? ""
        : payload.recent_pest_activity_answer || "",

    recent_pest_activity_comment:
      venueClosed
        ? ""
        : payload.recent_pest_activity_comment || "",

    fire_safety_checks:
      venueClosed
        ? {}
        : payload.fire_safety_checks || {},

    checklist_version:
      payload?.checklist_version || 3,

    completed_by_uid:
      auth?.currentUser?.uid || "",

    completed_by_email:
      auth?.currentUser?.email || "",

    submitted_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

async function handleStartDayCheckSubmit(payload) {
  if (startDisabled.value) return;

  const venue = cleanVenueId.value;
  const date = cleanTradingDate.value;

  error.value = "";
  busyStart.value = true;

  try {
    // Save the opening or closed status first.
    await saveStartDayCheck(
      payload,
      venue,
      date
    );

    // Original Start Day / Start Week callable.
    await httpsCallable(
      fns(),
      "startTradingDay"
    )({
      venueId: venue,
      tradingDate: date,
    });

    startDayCheckModalRef.value?.close();
    startDayCheckModalRef.value?.resetForm();
  } catch (e) {
    console.error("Start Day failed:", e);

    error.value =
      e?.message || String(e);
  } finally {
    busyStart.value = false;
  }
}

// ---------------------------------------------------------
// SAVE CLOSING CHECKS & END DAY
// ---------------------------------------------------------

async function handleEndDayCheckSubmit(payload) {
  if (
    !hasBasics.value ||
    busyEnd.value ||
    busyStart.value ||
    isOpen.value !== true
  ) {
    return;
  }

  const venue = cleanVenueId.value;
  const date = cleanTradingDate.value;
  const endWeek = isSunday.value;
  const venueClosed = payload?.venue_closed === true;

  error.value = "";
  busyEnd.value = true;

  let closingSaveFailed = false;

  try {
    if (venueClosed) {
      // NEW:
      // Write an explicit Venue Closed audit record.
      // The backend checks the original opening record,
      // so this cannot arbitrarily skip checks.
      await httpsCallable(
        fns(),
        "recordVenueClosedClosing"
      )({
        venueId: venue,
        tradingDate: date,
      });
    } else {
      // Original closing checklist workflow.
      // An unanswered item remains Not Completed.
      try {
        await httpsCallable(
          fns(),
          "fireSafetyApi"
        )({
          action: "saveClosing",
          venueId: venue,
          tradingDate: date,
          checks: payload?.checks || {},
        });
      } catch (e) {
        closingSaveFailed = true;

        console.error(
          "Closing checklist could not be saved:",
          e
        );
      }
    }

    // Original End Day / End Week callable.
    // Banking, day advancement and weekly pack remain intact.
    await httpsCallable(
      fns(),
      "endTradingDay"
    )({
      venueId: venue,
      tradingDate: date,
      endWeek,
    });

    endDayCheckModalRef.value?.close();
    endDayCheckModalRef.value?.resetForm();

    if (closingSaveFailed) {
      error.value =
        "End Day completed. The closing checklist " +
        "could not be saved before closure. " +
        "An incomplete audit record will be attempted " +
        "automatically. Please check Closing Checks.";
    }
  } catch (e) {
    console.error("End Day failed:", e);

    error.value =
      e?.message || String(e);
  } finally {
    busyEnd.value = false;
  }
}
</script>

<style scoped>
.day-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

.day-actions__buttons {
  min-width: 0;
  max-width: 100%;
}

.day-btn {
  line-height: 1 !important;
  padding: 0 16px !important;
  border-radius: 10px !important;
  white-space: nowrap;
  margin: 0 !important;
}

.day-btn__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.day-btn--start {
  background: #cfd8dc !important;
  color: #37474f !important;
}

.day-btn--start:hover {
  background: #b0bec5 !important;
}

.day-btn--end {
  background: #00897b !important;
  color: #ffffff !important;
}

.day-btn--end:hover {
  background: #00796b !important;
}

.day-actions__message {
  width: 100%;
  text-align: right;
}

.day-btn.disabled,
.day-btn:disabled {
  pointer-events: none;
  opacity: 0.55 !important;
}

@media (max-width: 992px) {
  .day-actions {
    align-items: center;
  }

  .day-actions__buttons {
    justify-content: center;
  }

  .day-actions__message {
    text-align: center;
  }
}

@media (max-width: 600px) {
  .day-actions {
    width: 100%;
    align-items: stretch;
  }

  .day-actions__message {
    text-align: left;
  }

  .day-btn {
    white-space: normal;
  }
}

@media (max-width: 420px) {
  .day-btn {
    font-size: 13px;
    padding: 0 12px !important;
  }
}
</style>
